/**
 * useGapCommit — the Quick-update card's choose-then-commit mechanic, as a
 * hook, so the three surfaces that offer it (the Quick-update card itself,
 * the Profile Catch/Raid radar strips, the Calendar event gap strips) share
 * ONE implementation instead of three lookalikes that drift.
 *
 * The contract (see EventCatchUp.tsx for the full design rationale):
 *   - A tap CHOOSES a tile; nothing is written. A second tap unchooses.
 *   - One commit writes every chosen gap through setManualWithLinks — the
 *     same path the manual add sheets use, so a shiny tick cascades into the
 *     Main dex and syncs like any other mark.
 *   - Every write is reversible: the commit toast carries an Undo, the caller
 *     renders an "Undo all", and a committed tile taps back to exactly its
 *     prior state — BOTH rows are snapshotted before the write, so undoing a
 *     shiny also removes the Main-dex row the cascade created (and only if it
 *     did not exist before).
 *
 * The CALLER decides what the gap list is and must keep it stable across
 * commits (frozen per event set) — a live gap recompute would delete the tile
 * just added and take its undo with it.
 */
import { useState } from "react";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { getDexEntryState, setManual, setManualWithLinks } from "@/lib/dexStateStore";
import { DYNAMAX_FORM_ID } from "@/lib/dynamaxDex";
import { isMegaFormId, MEGA_FORM_ID } from "@/lib/megaDex";
import { isCustomDexLinkageEnabled } from "@/lib/customDexLinkagePref";
import type { CellState } from "@/lib/pogoPokedexVariantClassifier";
import type { RaidDexVariant } from "@/lib/raidDexCrossRef";

/**
 * The dex a scoped copy implies, the cascade the form engine cannot know: a
 * Shiny Dynamax is a Dynamax and a 100% Mega is a Mega, so the species page's
 * Dynamax / Mega view ticks its DEFAULT tile when any copy is ticked
 * (dynamaxDex.toggleDynamaxTile, megaDex.toggleMegaTile). The gap tiles write
 * the same rows those views do, so they carry the same rule. Returns the
 * implied row's (variant, formId), or null for a costume / regional form.
 */
function impliedKindRow(
  variant: RaidDexVariant,
  formId?: string,
): { variant: RaidDexVariant; formId?: string } | null {
  if (!formId) return null;
  if (formId === DYNAMAX_FORM_ID) return variant === "dynamax" ? null : { variant: "dynamax" };
  if (isMegaFormId(formId)) {
    if (variant === "mega") return null;
    return { variant: "mega", ...(formId === MEGA_FORM_ID ? {} : { formId }) };
  }
  return null;
}

/** The shape every gap strip already carries. */
export interface GapLike {
  variant: RaidDexVariant;
  speciesId: number;
  /** Set for a form-scoped gap (a costume/regional linked to the event): the
   * write targets the FORM's own dex row, and its cascade fills the base. */
  formId?: string;
}

/**
 * What a tick wrote, so an untick can put it all back. A tick is not one
 * write: marking a shiny CASCADES into the Main dex, so an undo that only
 * cleared the shiny would leave a phantom Main-dex catch behind. `null`
 * means there was no entry at all, and `unknown` is how setManual is told
 * to clear an override back to exactly that.
 */
export type PriorState = {
  variant: CellState | null;
  pokemon: CellState | null;
  /** Form gaps only: the BASE-species rows the form cascade fills. Snapshotted
   * so an undo takes the cascaded species-level marks back too — the same
   * "exact prior state, cascade included" contract the base gaps keep. */
  baseVariant?: CellState | null;
  basePokemon?: CellState | null;
  /** Dynamax / Mega copies only: the kind's own dex row the copy implies
   * (see impliedKindRow), so an undo takes that tick back too. */
  kindDex?: CellState | null;
};

/** A gap key, unique per (variant, species, form) so one species can hold a
 * tile per variant AND a tile per linked costume. */
export function gapKey(variant: RaidDexVariant, speciesId: number, formId?: string): string {
  return formId ? `${variant}:${speciesId}:${formId}` : `${variant}:${speciesId}`;
}

/** Snapshot a gap's rows BEFORE a write, so undo restores, never guesses. For
 * a form gap the snapshot is the FORM's own rows — the same rows the write and
 * its cascade touch. */
export function snapshotPriorState(
  variant: RaidDexVariant,
  speciesId: number,
  formId?: string,
): PriorState {
  return {
    variant: getDexEntryState(variant, speciesId, formId)?.state ?? null,
    pokemon: getDexEntryState("pokemon", speciesId, formId)?.state ?? null,
    // A form write's cascade also lights the BASE species rows, so those are
    // part of what an undo has to put back.
    ...(formId
      ? {
          baseVariant: getDexEntryState(variant, speciesId)?.state ?? null,
          basePokemon: getDexEntryState("pokemon", speciesId)?.state ?? null,
        }
      : {}),
    ...(() => {
      const implied = impliedKindRow(variant, formId);
      return implied
        ? { kindDex: getDexEntryState(implied.variant, speciesId, implied.formId)?.state ?? null }
        : {};
    })(),
  };
}

/** Put a tile's rows back exactly as they were before it was ticked. */
export function restore(
  variant: RaidDexVariant,
  speciesId: number,
  prior: PriorState,
  formId?: string,
) {
  setManual(variant, speciesId, prior.variant ?? "unknown", formId);
  if (variant !== "pokemon") setManual("pokemon", speciesId, prior.pokemon ?? "unknown", formId);
  if (formId) {
    // The cascaded base-species rows: only touched when the snapshot carries
    // them, which it does for every write made after this field existed.
    if ("baseVariant" in prior) {
      setManual(variant, speciesId, prior.baseVariant ?? "unknown");
    }
    if (variant !== "pokemon" && "basePokemon" in prior) {
      setManual("pokemon", speciesId, prior.basePokemon ?? "unknown");
    }
    const implied = impliedKindRow(variant, formId);
    if (implied && "kindDex" in prior) {
      setManual(implied.variant, speciesId, prior.kindDex ?? "unknown", implied.formId);
    }
  }
}

/**
 * After a scoped copy is written, make sure the dex it implies is ticked:
 * you cannot own a Shiny Dynamax without a Dynamax. The Dynamax row goes
 * through setManualWithLinks so the Dynamax dex's Main linkage applies,
 * mirroring the species page's tile; a Mega row is a plain write.
 */
function ensureImpliedKindRow(variant: RaidDexVariant, speciesId: number, formId?: string): void {
  const implied = impliedKindRow(variant, formId);
  if (!implied) return;
  if (getDexEntryState(implied.variant, speciesId, implied.formId)?.state === "present") return;
  if (implied.variant === "dynamax") {
    setManualWithLinks("dynamax", speciesId, "present", { propagate: isCustomDexLinkageEnabled() });
  } else {
    setManual(implied.variant, speciesId, "present", implied.formId);
  }
}

/** Idle, chosen-but-not-written, or in the dex. */
export type TileState = "idle" | "selected" | "added";

export function useGapCommit(gaps: readonly GapLike[]): {
  /** Which of the three states a tile renders in. */
  tileState: (g: GapLike) => TileState;
  /** Choose / unchoose an idle tile; take back a committed one. */
  tap: (g: GapLike) => void;
  /** Write every chosen gap; announces a toast with Undo. */
  commit: () => void;
  /** Put every write from this strip back, in one go. */
  undoAll: () => void;
  selectedCount: number;
  addedCount: number;
} {
  const { toast } = useToast();
  /** Chosen, not yet written. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Written, with what each one overwrote so it can be put back. */
  const [added, setAdded] = useState<Map<string, PriorState>>(new Map());

  const undoBatch = (batch: Map<string, PriorState>) => {
    for (const g of gaps) {
      const prior = batch.get(gapKey(g.variant, g.speciesId, g.formId));
      if (prior) restore(g.variant, g.speciesId, prior, g.formId);
    }
    setAdded(new Map());
  };

  const tap = (g: GapLike) => {
    const id = gapKey(g.variant, g.speciesId, g.formId);
    const prior = added.get(id);
    // Already in the dex: this tap is the per-tile undo.
    if (prior) {
      restore(g.variant, g.speciesId, prior, g.formId);
      const next = new Map(added);
      next.delete(id);
      setAdded(next);
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const commit = () => {
    const written = new Map(added);
    // Tiles whose write did not persist stay CHOSEN, not "added": setManual
    // reports a durable-write failure (blocked/quota localStorage), and
    // toasting "Added" over one is exactly the silent quick-add loss this
    // mechanic exists to prevent. A kept selection means the button stays on
    // screen and a retry re-persists the whole store.
    const failed = new Set<string>();
    let count = 0;
    for (const g of gaps) {
      const id = gapKey(g.variant, g.speciesId, g.formId);
      if (!selected.has(id)) continue;
      // Snapshot BEFORE the write, so undo can restore rather than guess.
      const prior = snapshotPriorState(g.variant, g.speciesId, g.formId);
      const ok = g.formId
        ? // A form tile writes the FORM's own row; setManual's form cascade
          // fills the base-species rows the way the sub-dex sheets do.
          setManual(g.variant, g.speciesId, "present", g.formId)
        : setManualWithLinks(g.variant, g.speciesId, "present");
      if (!ok) {
        failed.add(id);
        continue;
      }
      if (g.formId) ensureImpliedKindRow(g.variant, g.speciesId, g.formId);
      written.set(id, prior);
      count += 1;
    }
    setAdded(written);
    setSelected(failed);
    if (count > 0) {
      toast({
        title: `Added ${count} to your Nexus Dex`,
        description: "Wrong one? Undo puts it back exactly as it was.",
        duration: 10000,
        action: (
          <ToastAction altText="Undo the catches just added" onClick={() => undoBatch(written)}>
            Undo
          </ToastAction>
        ),
      });
    }
    if (failed.size > 0) {
      toast({
        variant: "destructive",
        title: `Couldn't save ${failed.size === count + failed.size ? "your Nexus Dex update" : `${failed.size} of them`}`,
        description: "Your device blocked the write. They stay chosen: tap Add again to retry.",
        duration: 10000,
      });
    }
  };

  return {
    tileState: (g) => {
      const id = gapKey(g.variant, g.speciesId, g.formId);
      return added.has(id) ? "added" : selected.has(id) ? "selected" : "idle";
    },
    tap,
    commit,
    undoAll: () => undoBatch(added),
    selectedCount: selected.size,
    addedCount: added.size,
  };
}
